import { getRole } from '@/store/selectors';
import { getApplicationTaskGroups, getRouteStatusLoading, normalizeTaskRoute } from '@/store/apply/selectors';
import { hasPerm, P } from '@/utilities/roles/permissions';
import { useCallback, useMemo } from 'react';
import { Badge, Box, Button, Skeleton, Stack } from '@chakra-ui/react';
import { faArrowLeft, faBook, faCheckSquare, faClock, faHome } from '@fortawesome/pro-regular-svg-icons';
import { useSelector } from 'react-redux';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Icon, Span, Title } from 'shared-components';
import { ApplicationStatus } from 'types';
import { ApplicationState } from '../../../store';
import TrapTabs from '../../atoms/trap-tabs';
import ProgrammesLeftNav from '../programmesLeftNav';
import { useDocumentCounts } from '../../../hooks/useApplicantDocuments';
import { getUniViewApplicantTaskGroupsWithDocs } from '@/components/pages/applicant-dashboard/sections';

interface Props {
  userId: string;
  applicationId: string;
  givenName: string;
  middleName?: string;
  familyName: string;
  uni?: string;
}

function ApplicantLeftNav({ userId, applicationId, givenName, middleName, familyName, uni }: Props) {
  const location = useLocation();

  const role = useSelector(getRole);
  const canEdit = hasPerm(role, P.EDIT_APPLY_OFFER);

  const isActiveRoute = useCallback(
    route => {
      return location.pathname === route;
    },
    [location.pathname],
  );

  const { application, applicant } = useSelector((state: ApplicationState) => state.apply);
  const { applyConfig } = useSelector((state: ApplicationState) => state.applyConfig);
  const offersCount = useSelector((state: ApplicationState) => state.apply.offers.length);

  // Get dynamic task groups from route status API
  const routeStatusTaskGroups = useSelector(getApplicationTaskGroups);
  const isRouteStatusLoading = useSelector(getRouteStatusLoading);

  const isNotSubmitted = [ApplicationStatus.InProgress, ApplicationStatus.Completed].includes(
    application.applicationStatus,
  );

  const allSections = useMemo(
    () => [
      {
        id: 'summary',
        label: 'Summary',
        subLabel: '',
        description: 'Summary',
        status: 'Available',
        icon: faHome,
        count: null,
        route: `/application/${uni}/${userId}/${applicationId}/summary`,
      },
      {
        id: 'application',
        label: 'Application',
        subLabel: '',
        description: 'Application',
        status: 'Available',
        icon: faCheckSquare,
        count: null,
        route: `/application/${uni}/${userId}/${applicationId}/sections`,
      },
      {
        id: 'programmes',
        label: 'Programmes',
        subLabel: '',
        description: 'Programmes',
        status: 'Available',
        icon: faBook,
        count: offersCount,
        route: `/application/${uni}/${userId}/${applicationId}/programmes`,
      },
      {
        id: 'activityLog',
        label: 'Activity log',
        subLabel: '',
        description: 'Activity log',
        status: 'Available',
        icon: faClock,
        count: null,
        route: `/application/${uni}/${userId}/${applicationId}/activity-log`,
      },
    ],
    [uni, userId, applicationId, offersCount],
  );

  const mainSections = useMemo(() => {
    let sections = allSections;
    if (isNotSubmitted)
      sections = sections.filter(section => section.id !== 'application' && section.id !== 'programmes');
    if (!canEdit) sections = sections.filter(section => section.id !== 'activityLog');
    return sections;
  }, [allSections, isNotSubmitted]);

  const identityCount = useDocumentCounts(['passport', 'photoId']);
  const academicTranscriptCount = useDocumentCounts(['academicTranscript']);
  const cvCount = useDocumentCounts(['cv']);
  const englishTestCount = useDocumentCounts(['englishTest']);
  const referenceCount = useDocumentCounts(['referenceLetter']);
  const personalStatementCount = useDocumentCounts(['personalStatement']);
  const researchProposalCount = useDocumentCounts(['researchProposal']);

  const applicationTabs = useMemo(() => {
    if (isNotSubmitted) return [];

    // Use dynamic task groups from route status API if available, fallback to static config
    const taskGroups =
      routeStatusTaskGroups.length > 0
        ? routeStatusTaskGroups.map(group => ({
            label: group.label,
            tasks: group.tasks.map(task => ({
              label: task.label,
              route: normalizeTaskRoute(task.route),
            })),
          }))
        : getUniViewApplicantTaskGroupsWithDocs(application, applyConfig);

    return taskGroups.map(taskGroup => ({
      label: taskGroup.label,
      tabs: taskGroup.tasks.map(task => {
        const base = {
          id: `/application/${uni}/${userId}/${applicationId}${task.route}`,
          label: task.label,
          withIcon: true,
        };
        if (task.route === '/identity') {
          return { ...base, count: identityCount };
        }
        if (task.route === '/education-history') {
          return { ...base, count: academicTranscriptCount };
        }
        if (task.route === '/employment') {
          return { ...base, count: cvCount };
        }
        if (task.route === '/english-language') {
          return { ...base, count: englishTestCount };
        }
        if (task.route === '/references') {
          return { ...base, count: referenceCount };
        }
        if (task.route === '/personal-statement') {
          return { ...base, count: personalStatementCount };
        }
        if (task.route === '/research-proposal') {
          return { ...base, count: researchProposalCount };
        }
        return { ...base, count: 'docCount' in task ? task.docCount : undefined };
      }),
    }));
  }, [
    uni,
    userId,
    applicationId,
    application,
    applyConfig,
    routeStatusTaskGroups,
    identityCount,
    academicTranscriptCount,
    cvCount,
    englishTestCount,
    referenceCount,
    personalStatementCount,
  ]);

  const title = useMemo(() => {
    let fullName = '';
    if (application.givenName) {
      fullName = `${application.givenName} ${application.familyName ?? ''}`;
    } else if (applicant.givenName) {
      fullName = `${applicant.givenName} ${applicant.familyName ?? ''}`;
    } else {
      fullName = applicant.knownBy;
    }
    return fullName;
  }, [givenName, middleName, familyName, userId]);

  const isApplicationTab = applicationTabs.some(group => group.tabs.some(tab => isActiveRoute(tab.id)));

  return (
    <>
      <Title title={title} />

      <Stack spacing={0} pt={4} pb={6} px={'sidebarPxSmall'} overflowY={'auto'} className="nav-scrollbar">
        {!isApplicationTab ? (
          <>
            {mainSections.map(section => (
              <Button
                as={RouterLink}
                to={section.route}
                data-selector={section.id}
                variant={'sidebar'}
                size={'md'}
                key={section.id}
                isActive={isActiveRoute(section.route)}
              >
                <Span display="flex" justifyContent="flex-start" width="100%">
                  <Icon icon={section.icon} fixedWidth mr={2} />
                  {section.label}
                </Span>

                {section.count !== null && (
                  <Badge size="sm" variant={'pill'}>
                    {section.count}
                  </Badge>
                )}
              </Button>
            ))}

            {!isNotSubmitted && <ProgrammesLeftNav uni={uni} userId={userId} applicationId={applicationId} />}
          </>
        ) : (
          <Box pt={1}>
            <Button
              as={RouterLink}
              to={mainSections[1]?.route || mainSections[0].route}
              size={'sm'}
              leftIcon={<Icon icon={faArrowLeft} />}
              variant={'ghost'}
              mb={3}
              ml={0}
            >
              Application
            </Button>

            {isRouteStatusLoading ? (
              <Stack spacing={3}>
                {[1, 2, 3, 4].map(i => (
                  <Box key={i}>
                    <Skeleton height="16px" width="120px" mb={2} />
                    <Stack spacing={1}>
                      <Skeleton height="32px" />
                      <Skeleton height="32px" />
                      <Skeleton height="32px" />
                    </Stack>
                  </Box>
                ))}
              </Stack>
            ) : (
              applicationTabs.map((tab, index) => (
                <Box key={index} mb={4}>
                  <TrapTabs
                    tabs={tab.tabs}
                    onTabSelect={() => {}}
                    label={tab.label}
                    activeTab={location.pathname}
                    linkAs={RouterLink}
                  />
                </Box>
              ))
            )}
          </Box>
        )}
      </Stack>
    </>
  );
}

export default ApplicantLeftNav;
